import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import DataTable, {
  PAGE_SIZES,
} from "../../components/carbon/DataTable/DataTable";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import { NetworkError } from "../../components";
import OrderStateFilter, { OrderStateOption } from "./OrderStateFilter";
import OwnerFilter from "../../components/OwnerFilter/OwnerFilter";
import {
  buildOrderRows,
  navigateToOrder,
  orderExpandedContent,
  orderListHeaders,
} from "./orderListRows";
import useDebounce from "../../hooks/useDebounce";
import { useAllOrders, type AllOrdersParams } from "../../hooks/useOrders";
import { useUser } from "../../context";
import { OrderState } from "../../types";
import { BIDDING_LABELS, PILL_LABELS } from "../../utils";
import "./orders.scss";

/**
 * Rebuilt on the Carbon primitives layer (Task 4, Orders + Bidding flow) on `PageHeader`/
 * `DataTable`/`EmptyState`, following `Suppliers.tsx`/`Materials.tsx` as the reviewed list-screen
 * shape — same server-side search + pagination law (never client-filter an already-fetched page).
 *
 * Replaces the pre-Carbon `material-react-table` instance (`useMaterialReactTable`, column
 * sorting/filtering/expand-collapse). Two scope calls from Task 3's gap check, both carried
 * through here:
 *  - Column sorting is dropped (no migrated list — Users/Sites/Materials/Suppliers — has it);
 *    the server-side default order (newest `startDate` first) is now a fixed param instead of a
 *    user-toggleable column, see `apiParams` below.
 *  - JOINT orders' MRT expand/collapse sub-rows are flattened into always-visible rows —
 *    `orderListRows.tsx`'s own comment covers the row shape.
 *
 * Row/column building and the JOINT-order flatten are shared with `CompanyOrders.tsx` via
 * `orderListRows.tsx` (the two screens are near-byte-identical pre-Carbon, differing only in
 * which hook they call and their state-filter option set/heading/add-action) and
 * `OrderStateFilter.tsx` (the one working MRT column filter, rebuilt as a filter bar above
 * `DataTable` per Task 3's composition guidance).
 */
const Orders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isManager } = useUser();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm } = useDebounce(searchTerm, 500);
  const [selectedStates, setSelectedStates] = useState<OrderState[]>([]);
  // Company-wide list, defaulted to the logged-in user; clearing the owner filter shows everyone.
  const [selectedOwnerIds, setSelectedOwnerIds] = useState<string[]>(
    user?.id ? [user.id] : []
  );

  // A new search, state or owner filter always restarts at the first page — same convention
  // Sites.tsx/Users.tsx/Materials.tsx use.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedStates, selectedOwnerIds]);

  const apiParams: AllOrdersParams = useMemo(
    () => ({
      page,
      size: pageSize,
      ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
      ...(selectedStates.length > 0 ? { states: selectedStates } : {}),
      ...(selectedOwnerIds.length > 0 ? { userIds: selectedOwnerIds } : {}),
      // Fixed server-side default order (was the MRT table's initial, and only reachable,
      // sort state) — no longer user-toggleable, see this file's own top comment.
      sortBy: "startDate",
      sortDirection: "DESC",
    }),
    [page, pageSize, debouncedSearchTerm, selectedStates, selectedOwnerIds]
  );

  const { data: ordersData, isLoading, error } = useAllOrders(apiParams);

  const rows = useMemo(() => buildOrderRows(ordersData), [ordersData]);
  const headers = useMemo(() => orderListHeaders(t), [t]);

  const stateOptions: OrderStateOption[] = useMemo(
    () => [
      { value: OrderState.OPEN, label: BIDDING_LABELS.IN_PROGRESS },
      { value: OrderState.PENDING, label: BIDDING_LABELS.SCHEDULED },
      { value: OrderState.CONSULTATION, label: BIDDING_LABELS.CONSULTATION },
      { value: OrderState.SUCCEED, label: BIDDING_LABELS.SUCCESS },
      {
        value: OrderState.CLOSED,
        label: PILL_LABELS.OFFER_PENDING_OF_SELECTION,
      },
      { value: OrderState.CANCELLED, label: BIDDING_LABELS.CANCELLED },
    ],
    []
  );

  const addAction = (
    <Button as={Link} to="/orders/new" renderIcon={Add}>
      {t("navigation.newTender")}
    </Button>
  );

  const empty = <EmptyState title={t("orders.noOrdersFound")} />;

  return (
    <div>
      <PageHeader title={t("navigation.tenders")} action={addAction} />

      <div className="orders__filters">
        {/* Only managers can look across owners; everyone else stays on their own orders. */}
        {isManager && (
          <OwnerFilter
            id="orders-owner"
            selectedOwnerIds={selectedOwnerIds}
            onChange={setSelectedOwnerIds}
          />
        )}
        <OrderStateFilter
          idPrefix="orders"
          options={stateOptions}
          selected={selectedStates}
          onChange={setSelectedStates}
        />
      </div>

      {error ? (
        <NetworkError />
      ) : (
        <DataTable
          headers={headers}
          rows={rows}
          isLoading={isLoading}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            label: t("cta.search"),
          }}
          pagination={{
            // DataTable's Pagination (Carbon) is 1-indexed; this screen's own paging state — and
            // the `useOrders` query it drives — stays 0-indexed to match the API, same boundary
            // conversion Sites.tsx/Users.tsx/Materials.tsx use.
            page: page + 1,
            pageSize,
            pageSizes: PAGE_SIZES,
            totalItems: ordersData?.totalElements ?? 0,
            onPageChange: (newPage, newPageSize) => {
              setPage(newPage - 1);
              setPageSize(newPageSize);
            },
          }}
          onRowClick={(row) => navigateToOrder(navigate, row)}
          getExpandedContent={(row) => orderExpandedContent(row, t)}
          empty={empty}
        />
      )}
    </div>
  );
};

export default Orders;
